import {
  Alert,
  Box,
  Container,
  Divider,
  Paper,
  Snackbar,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import ReleaseDropzone from '../components/ReleaseDropzone';
import ReleaseList from '../components/ReleaseList';
import appReleasesService from '../services/app-releases-service';

const PLATFORMS = ['android', 'ios'];

function AppReleasesPage() {
  const [tab, setTab] = useState(0);
  const platform = PLATFORMS[tab];

  const [releases, setReleases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const notify = useCallback(
    (message, severity = 'success') =>
      setSnackbar({ open: true, message, severity }),
    []
  );

  const fetchReleases = useCallback(async () => {
    setLoading(true);
    try {
      const res = await appReleasesService.getReleases(platform);
      setReleases(res.success ? res.data : []);
    } catch (err) {
      setReleases([]);
      notify('Failed to load releases.', 'error');
    } finally {
      setLoading(false);
    }
  }, [platform, notify]);

  useEffect(() => {
    fetchReleases();
  }, [fetchReleases]);

  const handleUploadSuccess = (release) => {
    notify(
      `Uploaded ${platform} v${release.versionName} (code ${release.versionCode}).`
    );
    fetchReleases();
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={2}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          📱 App Releases (OTA)
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Upload Android (.apk) and iOS (.ipa) builds. The mobile app checks the
          latest release on launch and self-updates. Set a build as “latest” to
          roll it out; mark it “mandatory” to force the update.
        </Typography>
      </Box>

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{ mb: 3 }}
        textColor="primary"
        indicatorColor="primary"
      >
        <Tab label="Android (.apk)" />
        <Tab label="iOS (.ipa)" />
      </Tabs>

      <Paper variant="outlined" sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Upload new {platform} release
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ReleaseDropzone
          key={platform}
          platform={platform}
          onUploadSuccess={handleUploadSuccess}
        />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Release history
        </Typography>
        <Divider sx={{ mb: 2 }} />
        <ReleaseList
          releases={releases}
          loading={loading}
          platform={platform}
          onChanged={fetchReleases}
          notify={notify}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default AppReleasesPage;
